import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import clsx from 'clsx';
import { api } from '../../shared/api';
import { fmtDate } from '../../shared/dates';
import {
  IntakeCandidateStatuses,
  IntakeCandidateStatusLabels,
  type IntakeCandidateStatus,
} from '@census/shared';

/**
 * Intake Pipeline (CRM-style board).
 *
 * EMR pushes prospective clients into POST /intake-candidates. Staff see them
 * here, work them through statuses, and click "Promote to intake" → routes to
 * the Intake Wizard pre-populated with the candidate data. On commit the
 * candidate is flagged CONVERTED automatically (by matching externalRef), so
 * the same person can't be intaked twice from a stale EMR feed.
 */
export function PipelinePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<IntakeCandidateStatus | ''>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['intake-candidates', statusFilter, includeArchived, q],
    queryFn: () =>
      api
        .get('/intake-candidates', {
          params: {
            status: statusFilter || undefined,
            includeArchived: includeArchived ? 'true' : undefined,
            q: q || undefined,
          },
        })
        .then((r) => r.data),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) =>
      api.patch(`/intake-candidates/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['intake-candidates'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Update failed'),
  });

  // Group by status for the board layout.
  const byStatus = useMemo(() => {
    const out: Record<string, any[]> = {};
    for (const s of IntakeCandidateStatuses) out[s] = [];
    for (const c of (data ?? []) as any[]) {
      (out[c.status] ??= []).push(c);
    }
    return out;
  }, [data]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Intake Pipeline</h1>
          <p className="text-sm text-slate-500">
            Prospective clients fed in by the EMR. Promote a candidate to launch the intake
            wizard pre-populated — once you commit, the candidate is auto-flagged{' '}
            <span className="font-semibold">CONVERTED</span> so the same person can't be intaked
            twice.
          </p>
        </div>
      </header>

      <section className="card flex flex-wrap items-center gap-3 px-4 py-3">
        <input
          className="input h-9 w-60"
          placeholder="Search name / EMR id / email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="input h-9 w-44"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
        >
          <option value="">All statuses</option>
          {IntakeCandidateStatuses.map((s) => (
            <option key={s} value={s}>
              {IntakeCandidateStatusLabels[s]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Include archived
        </label>
        <span className="ml-auto text-xs text-ink-subtle">
          EMR pushes candidates via{' '}
          <code className="rounded bg-surface-muted px-1 py-0.5">POST /api/v1/intake-candidates</code>{' '}
          with <code>externalRef</code> as the unique key.
        </span>
      </section>

      {isLoading ? (
        <p className="text-sm text-ink-subtle">Loading…</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-3">
          {IntakeCandidateStatuses.map((s) => {
            const items = byStatus[s] ?? [];
            return (
              <section key={s} className="card flex min-h-[200px] flex-col p-3">
                <header className="mb-2 flex items-center justify-between text-sm font-semibold">
                  <span>{IntakeCandidateStatusLabels[s]}</span>
                  <span className={statusCountCls(s)}>{items.length}</span>
                </header>
                <ul className="space-y-2">
                  {items.map((c: any) => (
                    <li key={c.id} className="rounded border border-line-subtle bg-white p-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-2xs text-ink-subtle">{c.externalRef}</span>
                        {c.status === 'CONVERTED' && c.clientCohortId && (
                          <Link
                            className="text-2xs text-brand-700 hover:underline"
                            to={`/clients/${c.clientId}`}
                          >
                            {c.clientCohortId} ↗
                          </Link>
                        )}
                      </div>
                      <div className="mt-1 font-medium">
                        {c.firstName} {c.lastName}
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-x-2 text-2xs text-ink-subtle">
                        {c.suggestedCohort && <span>Cohort {c.suggestedCohort}</span>}
                        {c.dob && <span>DOB {c.dob}</span>}
                        {c.cellPhone && <span className="col-span-2">📱 {c.cellPhone}</span>}
                        {c.sourceLabel && <span className="col-span-2">↳ {c.sourceLabel}</span>}
                      </div>
                      {c.presentingConcerns && (
                        <p className="mt-1 line-clamp-2 text-2xs text-ink-muted">
                          {c.presentingConcerns}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-1">
                        {c.status !== 'CONVERTED' && (
                          <button
                            className="btn-primary h-6 px-2 text-2xs"
                            onClick={() => {
                              // Route to intake with state pre-fill. The wizard
                              // reads location.state and seeds the draft fields.
                              navigate('/intake', {
                                state: {
                                  fromCandidate: {
                                    candidateId: c.id,
                                    externalRef: c.externalRef,
                                    firstName: c.firstName,
                                    lastName: c.lastName,
                                    suggestedCohort: c.suggestedCohort ?? undefined,
                                  },
                                },
                              });
                            }}
                          >
                            Promote to intake →
                          </button>
                        )}
                        {c.status === 'NEW' && (
                          <button
                            className="btn-secondary h-6 px-2 text-2xs"
                            onClick={() =>
                              update.mutate({ id: c.id, patch: { status: 'CONTACTED' } })
                            }
                          >
                            Mark contacted
                          </button>
                        )}
                        {c.status !== 'DECLINED' && c.status !== 'CONVERTED' && (
                          <button
                            className="text-2xs text-rose-700 hover:underline"
                            onClick={() => {
                              if (confirm('Mark declined? This stays in history.')) {
                                update.mutate({ id: c.id, patch: { status: 'DECLINED' } });
                              }
                            }}
                          >
                            Decline
                          </button>
                        )}
                        {c.status !== 'DUPLICATE' && c.status !== 'CONVERTED' && (
                          <button
                            className="text-2xs text-amber-700 hover:underline"
                            onClick={() => update.mutate({ id: c.id, patch: { status: 'DUPLICATE' } })}
                          >
                            Duplicate
                          </button>
                        )}
                      </div>
                      <div className="mt-1 text-2xs text-ink-subtle">
                        Received {fmtDate(c.receivedAt.slice(0, 10))}
                      </div>
                    </li>
                  ))}
                  {items.length === 0 && (
                    <li className="rounded border border-dashed border-line-subtle p-2 text-2xs text-ink-subtle">
                      None
                    </li>
                  )}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function statusCountCls(s: string): string {
  return clsx(
    'inline-flex h-5 min-w-[20px] items-center justify-center rounded px-1.5 text-2xs font-semibold',
    s === 'NEW' && 'bg-brand-100 text-brand-800',
    s === 'CONTACTED' && 'bg-sky-100 text-sky-800',
    s === 'SCHEDULED' && 'bg-amber-100 text-amber-800',
    s === 'CONVERTED' && 'bg-emerald-100 text-emerald-800',
    s === 'DECLINED' && 'bg-slate-100 text-slate-700',
    s === 'DUPLICATE' && 'bg-rose-100 text-rose-800',
  );
}
